import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { MessageListQuery } from '../../__generated__/MessageListQuery.graphql'
import styled from 'styled-components'
import moment from 'moment'
import {
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@material-ui/core'
import useNiceMutation from '../../mutations/useNiceMutation'
import { MessageListSendMessageMutation } from '../../__generated__/MessageListSendMessageMutation.graphql'
import { MessageListAttachShipmentMutation } from '../../__generated__/MessageListAttachShipmentMutation.graphql'
import { MessageListMarkReadMutation } from '../../__generated__/MessageListMarkReadMutation.graphql'
import { useSnackbar } from 'notistack'
import { useDateTimeDisplay } from '../../hooks/timeZoneDisplay'
import useLazyLoadQueryWithSubscription from '../../hooks/useLazyLoadQueryWithSubscription'
import SessionContext from '../../SessionContext'
interface MessageListProps {
  selectedUser: {
    number: number
    name: string
    username: string
    roles: string[]
    unreadMessageCount: number
  } | null
  isFirstLoad: boolean
  setIsFirstLoad: React.Dispatch<React.SetStateAction<boolean>>
}

const MessagesList = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${(props) => props.theme.palette.background.default};
`

const InputContainer = styled.div`
  padding: 16px;
  border-top: 1px solid ${(props) => props.theme.palette.grey[300]};
  display: flex;
  gap: 8px;
  background-color: ${(props) => props.theme.palette.background.paper};
`

const MessageBubble = styled(Paper)<{ isSentByCurrentUser: boolean }>`
  max-width: 70%;
  align-self: ${(props) => (props.isSentByCurrentUser ? 'flex-end' : 'flex-start')};
  background-color: ${(props) => (props.isSentByCurrentUser ? props.theme.palette.primary.light : props.theme.palette.background.paper)};
  color: ${(props) => (props.isSentByCurrentUser ? props.theme.palette.primary.contrastText : props.theme.palette.text.primary)};
  padding: 12px;
  border-radius: 12px;
  border-top-left-radius: ${(props) => (props.isSentByCurrentUser ? '12px' : '0')};
  border-top-right-radius: ${(props) => (props.isSentByCurrentUser ? '0' : '12px')};
  display: flex;
  align-items: center;
  gap: 8px;
`

const AvatarContainer = styled(Avatar)`
  align-self: flex-start;
`

const Metadata = styled(Typography)`
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.text.secondary};
  margin-top: 4px;
`

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: ${(props) => props.theme.palette.background.paper};
  border-bottom: 1px solid ${(props) => props.theme.palette.background.paper};
`

const FiltersAndButton = styled.div`
  display: flex;
  align-items: center;
`

const WarningContainer = styled.div`
  padding: 16px;
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
  border-radius: 4px;
  margin-bottom: 8px;
`

const UploadableItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 16px;
`

const UploadableItem = styled(Paper).attrs({ variant: 'outlined' })`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 8px;
  padding: 8px;
`

const UploadablePreviewContainer = styled.div`
  height: 100px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const UploadablePreview = styled.img<{ $isIcon: boolean }>`
  height: ${(props) => (props.$isIcon ? '50px' : '100%')};
  width: ${(props) => (props.$isIcon ? '50px' : '100%')};
  object-fit: contain;
`

const AttachmentAvatar = styled('div')`
  width: 32px;
  height: 32px;
  background-color: #bdbdbd;
  color: #fff;
  font-weight: 600;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`
const ChatBubble = styled(Paper)<{ isSent: boolean }>`
  max-width: 70%;
  align-self: ${(props) => (props.isSent ? 'flex-end' : 'flex-start')};
  background-color: ${(props) => (props.isSent ? props.theme.palette.primary.light : props.theme.palette.grey[200])};
  color: ${(props) => (props.isSent ? props.theme.palette.primary.contrastText : props.theme.palette.text.primary)};
  padding: 12px;
  border-radius: 12px;
  border-top-left-radius: ${(props) => (props.isSent ? '12px' : '0')};
  border-top-right-radius: ${(props) => (props.isSent ? '0' : '12px')};
  display: flex;
  gap: 8px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
`

interface Uploadable {
  file: File
  id: string
  name: string
  contentType: string
  documentType?: string
}

const MessageListComponent: React.FC<MessageListProps> = ({ selectedUser, isFirstLoad, setIsFirstLoad }) => {
  const { user } = useContext(SessionContext)
  const [params, setParams] = useState({
    page: 1,
    perPage: 200,
    slug: selectedUser?.username ?? '',
  })
  const queryData = useLazyLoadQueryWithSubscription<MessageListQuery>(query, params, {
    subscriptionOptions: {
      entityType: 'contacts',
      entitySlug: user?.username || '',
    },
  })
  const IMAGE_FILE_SIZE_LIMIT = 1.5
  const VIDEO_FILE_SIZE_LIMIT = 15
  const [previewUrls, setPreviewUrls] = useState<{ [id: string]: string }>({})
  const [messageList, setMessageList] = useState(queryData.messageList)
  const [commitSendMessage, sendMessageIsInFlight] = useNiceMutation<MessageListSendMessageMutation>(sendMessageMutation)
  const [commitMarkRead] = useNiceMutation<MessageListMarkReadMutation>(markReadMutation)
  const [commitAttachShipment] = useNiceMutation<MessageListAttachShipmentMutation>(attachShipmentMutation)
  const [selectedContact, setSelectedContact] = useState<string | null>(null)
  const [isAssignMode, setIsAssignMode] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [newMessage, setNewMessage] = useState<string>('')

  const scrollRef: any = useRef({
    hasNextPage: false,
    hasPrevPage: false,
    page: 1,
    perPage: 200,
    totalPages: 1,
    isLoading: false,
  })

  useEffect(() => {
    if (!queryData?.messageList) return
    let newUpdatedData
    if (isFirstLoad) {
      newUpdatedData = { ...queryData.messageList }
      setMessageList(newUpdatedData)
    } else {
      const updatedMessageList = { ...messageList }
      newUpdatedData = {
        ...queryData.messageList,
        messages: [...queryData.messageList.messages, ...updatedMessageList?.messages],
        shipments: [...queryData.messageList.shipments, ...updatedMessageList?.shipments],
      }
      setMessageList(newUpdatedData)
    }
    scrollRef.current = {
      ...queryData.messageList?.paginationMeta,
      isLoading: false,
    }
    markMessagesAsRead(newUpdatedData)
    setSelectedContact(selectedUser?.number?.toString() || null)
  }, [queryData?.messageList, isFirstLoad])

  const { enqueueSnackbar } = useSnackbar()
  const [selectedMessages, setSelectedMessages] = useState<
    {
      slug: string | undefined
      body: string
      sentAt: string
      sentAtDisplay: string
      from: string
      previewStatus: string
      deliveryStatus: string
      senderSlug?: string
      direction: string
      entitySlug?: string | undefined
      attachments: {
        slug: string
        url: string
        name: string
        type: string
        contentType: string
        previewUrl: string
      }[]
    }[]
  >([])
  const [checkedMessages, setCheckedMessages] = useState<string[]>([])
  const [shipments, setShipments] = useState<{ slug: string; title: string; vinLine: string }[]>([])
  const [selectedShipment, setSelectedShipment] = useState<string | null>(null)
  const [sessionActive, setSessionActive] = useState<boolean>(true)
  const [shipmentSearchQuery, setShipmentSearchQuery] = useState<string>('')
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const messagesListRef = useRef<HTMLDivElement | null>(null)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [uploadables, setUploadables] = useState<Uploadable[]>([])
  const pdfLogo = `https://drive-thirdparty.googleusercontent.com/64/type/application/pdf`
  const handleImageLoad = () => {
    setIsImageLoaded(true)
  }

  const handleOpen = (url: string) => {
    window.open(url, '_blank')
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleOpen('')
    }
  }

  useEffect(() => {
    if (messagesEndRef.current && params?.page == 1) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [selectedMessages])

  useEffect(() => {
    if (messageList?.shipments) {
      setShipments(
        messageList?.shipments.map((shipment) => ({
          slug: shipment.slug,
          title: shipment.title || '',
          vinLine: shipment.vinLine || '',
        })) || []
      )
    }
  }, [messageList.shipments])

  useEffect(() => {
    if (selectedUser?.username) {
      setParams({
        page: 1,
        perPage: 200,
        slug: selectedUser.username,
      })
    }
  }, [selectedUser?.username])

  useEffect(() => {
    if (messageList.messages) {
      const messages =
        messageList?.messages.map((message: any) => ({
          slug: message.slug,
          body: message.body,
          sentAt: message.sentAt,
          sentAtDisplay: message.sentAtDisplay,
          from: message.from,
          direction: message.direction,
          previewStatus: message.previewStatus,
          deliveryStatus: message.deliveryStatus.toString(),
          senderSlug: message.senderSlug || undefined,
          entitySlug: message.entitySlug || undefined,
          attachments: message.attachments || [],
        })) || []
      setSelectedMessages(messages)
      checkSessionActive(messages)
    }
  }, [messageList.messages])

  const prevScrollHeightRef = useRef(0)

  useEffect(() => {
    if (messagesListRef.current && prevScrollHeightRef.current) {
      const messageListDiv = messagesListRef.current

      setTimeout(() => {
        const heightDiff = messageListDiv.scrollHeight - prevScrollHeightRef.current

        if (heightDiff > 0) {
          messageListDiv.scrollTop += heightDiff - 150
        }

        prevScrollHeightRef.current = messageListDiv.scrollHeight
      }, 100)
    }
  }, [params.page])

  useEffect(() => {
    const handleScroll = () => {
      if (!messagesListRef.current) return

      const messageListDiv = messagesListRef.current
      const scrollTop = messageListDiv.scrollTop
      const paginationData = { ...scrollRef?.current }

      if (scrollTop === 0 && paginationData?.hasNextPage && !paginationData?.isLoading) {
        scrollRef.current = { ...scrollRef?.current, isLoading: true }
        prevScrollHeightRef.current = messageListDiv.scrollHeight
        setIsFirstLoad(false)
        setParams((prev) => ({ ...prev, page: prev.page + 1 }))
      }
    }

    const messageListDiv = messagesListRef.current
    if (messageListDiv) {
      messageListDiv.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (messageListDiv) {
        messageListDiv.removeEventListener('scroll', handleScroll)
      }
    }
  }, [scrollRef.current?.isLoading]) // Re-run the effect when isLoading flag changes

  const sendMessage = () => {
    const uploadableFiles = uploadables
    if (!sessionActive) {
      enqueueSnackbar('User must send a message to activate the session.', {
        variant: 'warning',
      })
      return
    }
    const updatedState = {
      to: selectedContact ?? '',
      body: newMessage,
      attachments: uploadableFiles.map((uploadable) => ({
        identifier: uploadable.id,
        name: uploadable.name,
        documentType: uploadable.documentType,
      })),
    }
    commitSendMessage({
      variables: {
        input: updatedState,
      },
      uploadables: Object.fromEntries(uploadableFiles.map((uploadable) => [uploadable.id, uploadable.file])),
      onCompleted: (response, errors) => {
        if (errors) {
          return
        }

        // Create attachments array from uploadables
        const attachments = uploadableFiles.map((uploadable) => ({
          slug: '',
          url: previewUrls[uploadable.id] || '',
          name: uploadable.name,
          type: uploadable.documentType || 'Document',
          contentType: uploadable.contentType,
          previewUrl: previewUrls[uploadable.id] || '',
        }))

        // Create a single message with blank slug (slugs is always empty)
        const newMessageDetails = [
          {
            slug: '',
            body: newMessage,
            sentAt: new Date().toISOString(),
            sentAtDisplay: moment().fromNow(),
            from: 'doc@portagent.eu',
            previewStatus: 'sent',
            deliveryStatus: 'sent',
            direction: '1',
            attachments: attachments,
          },
        ]

        // Clear the form and uploadables
        setNewMessage('')
        setUploadables([])
        setPreviewUrls({})
        enqueueSnackbar('Message sent successfully', { variant: 'success' })
        setSelectedMessages((prevSelectedMessages: any) => [...prevSelectedMessages, ...newMessageDetails])
      },
    })
  }

  const filteredShipments = shipments.filter((shipment) => {
    return (
      shipment.title.toLowerCase().includes(shipmentSearchQuery.toLowerCase()) ||
      shipment.vinLine.toLowerCase().includes(shipmentSearchQuery.toLowerCase()) ||
      shipment.slug.toLowerCase().includes(shipmentSearchQuery.toLowerCase())
    )
  })

  const checkSessionActive = (messages: any) => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      // Extract ISO 8601 timestamp by removing 'on ' prefix
      const isoTimestamp = lastMessage.sentAt.replace(/^on\s/, '')
      const lastMessageDate = new Date(isoTimestamp) // Create Date object from isoTimestamp

      // Check if the date is valid
      if (isNaN(lastMessageDate.getTime())) {
        console.error('Invalid date format:', isoTimestamp)
        return
      }

      const lastMessageTime = lastMessageDate.getTime() // Get the timestamp in milliseconds
      const currentTime = Date.now() // current time in milliseconds

      // Compare the time difference in milliseconds
      const timeDifference = currentTime - lastMessageTime

      // Check if the message was sent within the last 24 hours
      const isActive = timeDifference <= 24 * 60 * 60 * 1000
      setSessionActive(isActive)
    }
  }

  const handleSaveAssignments = () => {
    if (checkedMessages.length > 0 && selectedShipment) {
      handleAssign()
    } else {
      enqueueSnackbar('Please select at least one message to assign.', {
        variant: 'warning',
      })
    }
  }

  const markMessagesAsRead = (data: any) => {
    const unreadMessages = data?.messages
      .filter((message: any) => message.previewStatus !== '2' && message.direction === '0')
      .map((message: any) => message.slug)
    if (unreadMessages && unreadMessages.length > 0) {
      commitMarkRead({
        variables: {
          input: {
            messageSlugs: unreadMessages,
            userSlug: selectedUser?.username ?? '',
          },
        },
      })
    }
  }

  const handleAssign = () => {
    commitAttachShipment({
      variables: {
        input: {
          messageSlugs: checkedMessages,
          shipmentSlug: selectedShipment || '',
        },
      },
      onCompleted: (response, errors) => {
        if (errors) {
          enqueueSnackbar('Failed to assign messages to shipment', {
            variant: 'error',
          })
        } else {
          enqueueSnackbar('Messages assigned to shipment successfully', {
            variant: 'success',
          })
          setSelectedMessages((prevSelectedMessages) =>
            prevSelectedMessages.map((message) =>
              message.slug && checkedMessages.includes(message.slug) ? { ...message, entitySlug: selectedShipment || undefined } : message
            )
          )
        }
      },
      onError: () => {
        enqueueSnackbar('An error occurred while assigning messages to shipment', { variant: 'error' })
      },
    })
    setAssignModalOpen(false)
    setIsAssignMode(false)
    setSelectedShipment(null)
    setCheckedMessages([])
  }

  const toggleShipmentSelection = (shipmentSlug: string) => {
    setSelectedShipment(shipmentSlug)
  }

  const toggleMessageSelection = (messageSlug: string) => {
    setCheckedMessages((prev) => (prev.includes(messageSlug) ? prev.filter((slug) => slug !== messageSlug) : [...prev, messageSlug]))
  }

  const onFileInputChange = (incomingFiles: FileList | null) => {
    const files = incomingFiles || []
    const newUploadables: Uploadable[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]

      const fileSize = Number.parseFloat((file.size / 1024 / 1024).toFixed(4))

      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')

      if (fileSize > IMAGE_FILE_SIZE_LIMIT && isImage) {
        enqueueSnackbar('Image size cannot be greater than 1.5MB', { variant: 'error' })
        continue
      }
      if (fileSize > VIDEO_FILE_SIZE_LIMIT && isVideo) {
        enqueueSnackbar('Video size cannot be greater than 15MB', { variant: 'error' })
        continue
      }

      newUploadables.push({
        file: file,
        name: file.name,
        id: file.name,
        contentType: file.type,
        documentType: file.type.startsWith('image/') ? 'Image' : file.type.startsWith('video/') ? 'Video' : 'Document',
      })

      if (file.type.startsWith('image/')) {
        const fileReader = new FileReader()
        fileReader.onloadend = (evt: ProgressEvent) => {
          const reader: FileReader = evt.target as FileReader
          setPreviewUrls((prevState) => ({ ...prevState, [file.name]: reader.result as string }))
        }
        fileReader.readAsDataURL(file)
      } else if (file.type === 'application/pdf') {
        // Show PDF preview icon
        setPreviewUrls((prevState) => ({
          ...prevState,
          [file.name]: pdfLogo, // <-- Replace with your icon
        }))
      }
    }
    setUploadables((oldUploadables) => {
      const newState = [...oldUploadables, ...newUploadables]
      return newState
    })
  }

  const handleRemoveUploadable = (idToRemove: string) => {
    setUploadables((prevUploadables) => prevUploadables.filter((uploadable) => uploadable.id !== idToRemove))

    setPreviewUrls((prevPreviewUrls) => {
      const updated = { ...prevPreviewUrls }
      delete updated[idToRemove]
      return updated
    })
  }

  return (
    <>
      {/* Main Section */}

      {
        <>
          <FilterContainer>
            <Typography variant='h6'>All Messages</Typography>
            <FiltersAndButton>
              <Button
                variant='contained'
                color={isAssignMode ? 'secondary' : 'primary'}
                onClick={() => {
                  if (isAssignMode && selectedMessages.length > 0) {
                    setAssignModalOpen(true)
                  } else {
                    setIsAssignMode((prev) => !prev)
                  }
                }}
              >
                {isAssignMode ? (selectedMessages.length > 0 ? 'Assign' : 'Cancel') : 'Assign Message'}
              </Button>
              {isAssignMode && (
                <Button
                  onClick={() => {
                    setIsAssignMode(false)
                    setCheckedMessages([])
                  }}
                >
                  Cancel
                </Button>
              )}
            </FiltersAndButton>
          </FilterContainer>
          <MessagesList ref={messagesListRef}>
            {selectedMessages.map((message, index) => (
              <React.Fragment key={index}>
                {/* Main text message bubble */}
                {!(message.direction === '0' && message.attachments?.length > 0) && !(message.body === '') && (
                  <MessageBubble isSentByCurrentUser={message.direction === '1'}>
                    {!isAssignMode && <AvatarContainer>{message.senderSlug?.charAt(0)?.toUpperCase()}</AvatarContainer>}

                    {isAssignMode && (
                      <Checkbox
                        checked={message.slug ? checkedMessages.includes(message.slug) : false}
                        onChange={() => message.slug && toggleMessageSelection(message.slug)}
                      />
                    )}

                    <div>
                      <Typography>{message.body}</Typography>

                      <Metadata>
                        {useDateTimeDisplay(message.sentAt)}
                        {message.entitySlug && ` | Shipment: ${message.entitySlug}`}
                      </Metadata>
                    </div>
                  </MessageBubble>
                )}

                {/* Separate message bubbles for each attachment */}
                {message.attachments &&
                  message.attachments.map((attachment: any) => (
                    <ChatBubble key={attachment.slug} isSent={message.direction === '1'}>
                      {!isAssignMode && <AttachmentAvatar>{message.senderSlug?.charAt(0)?.toUpperCase()}</AttachmentAvatar>}
                      <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '250px' }}>
                        <div
                          role='button'
                          tabIndex={0}
                          onClick={() => handleOpen(attachment.url)}
                          onKeyDown={handleKeyDown}
                          style={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            backgroundColor: '#f0f0f0',
                            cursor: 'pointer',
                            width: '100%',
                            height: '160px',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {!isImageLoaded && <CircularProgress size={24} style={{ position: 'absolute' }} />}

                          {attachment.type === 'Image' ? (
                            <img
                              src={attachment.url}
                              alt={attachment.name || 'image'}
                              onLoad={handleImageLoad}
                              style={{
                                width: '100%',
                                height: '100%',
                                display: isImageLoaded ? 'block' : 'none',
                              }}
                            />
                          ) : (
                            <div style={{ textAlign: 'center' }}>
                              <img
                                src={pdfLogo}
                                alt='PDF'
                                onLoad={handleImageLoad}
                                style={{
                                  width: 64,
                                  height: 64,
                                  display: isImageLoaded ? 'block' : 'none',
                                }}
                              />
                            </div>
                          )}
                        </div>

                        {attachment.name && (
                          <div
                            style={{
                              fontSize: '13px',
                              color: '#333',
                              marginTop: '6px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {attachment.name}
                          </div>
                        )}

                        <div
                          style={{
                            fontSize: '12px',
                            color: '#888',
                            marginTop: 4,
                            alignSelf: 'flex-end',
                          }}
                        >
                          <Metadata>{useDateTimeDisplay(message.sentAt)}</Metadata>
                        </div>
                      </div>
                    </ChatBubble>
                  ))}
              </React.Fragment>
            ))}
            <div ref={messagesEndRef} />
          </MessagesList>

          {/* Input Box */}
          {!sessionActive && (
            <WarningContainer>
              <Typography> The 24-hour session is inactive. Please use the Shipment Page to send a template messages.</Typography>
            </WarningContainer>
          )}
          <UploadableItemsContainer>
            {uploadables.map((uploadable) => (
              <>
                <UploadableItem key={uploadable.id}>
                  <UploadablePreviewContainer>
                    <Button
                      onClick={() => handleRemoveUploadable(uploadable.id)}
                      style={{
                        position: 'absolute',
                        background: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    >
                      X
                    </Button>
                    <UploadablePreview src={previewUrls[uploadable.id]} alt={uploadable.name} $isIcon={!previewUrls[uploadable.id]} />
                  </UploadablePreviewContainer>
                </UploadableItem>
              </>
            ))}
          </UploadableItemsContainer>

          <InputContainer>
            <TextField
              fullWidth
              variant='outlined'
              placeholder='Type a message...'
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <label htmlFor='file-upload'>
              <input
                id='file-upload'
                type='file'
                hidden
                multiple
                accept={'.jpeg, .jpg, .png, application/pdf'}
                onChange={(e) => onFileInputChange(e.target.files)}
              />
              <Button variant='contained' component='span'>
                + Upload Files
              </Button>
            </label>
            <Button variant='contained' color='primary' onClick={sendMessage} disabled={sendMessageIsInFlight}>
              Send
            </Button>
          </InputContainer>
          <Dialog
            open={assignModalOpen}
            onClose={() => setAssignModalOpen(false)}
            PaperProps={{
              style: {
                marginTop: '-100px',
              },
            }}
          >
            <DialogTitle>Assign Messages to Shipment</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                variant='outlined'
                placeholder='Search shipments ...'
                value={shipmentSearchQuery}
                onChange={(e) => setShipmentSearchQuery(e.target.value)}
                style={{ marginBottom: '16px' }}
              />
              <List>
                {filteredShipments.map((shipment) => (
                  <ListItem key={shipment.slug}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={selectedShipment === shipment.slug} onChange={() => toggleShipmentSelection(shipment.slug)} />
                      }
                      label={
                        <>
                          <Typography>{shipment.title}</Typography>
                          <Metadata>{shipment.vinLine}</Metadata>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAssignModalOpen(false)} color='default'>
                Cancel
              </Button>
              <Button onClick={handleSaveAssignments} color='primary'>
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      }
    </>
  )
}

const query = graphql`
  query MessageListQuery($slug: String!, $page: Int, $perPage: Int) {
    messageList(slug: $slug, page: $page, perPage: $perPage) {
      messages {
        slug
        subject
        body
        sentAt
        sentAtDisplay
        senderSlug
        recipientSlugs
        to
        from
        entitySlug
        previewStatus
        deliveryStatus
        direction
        attachments {
          slug
          url
          name
          type
          contentType
          previewUrl
        }
      }
      shipments {
        slug
        title
        vinLine
      }
      paginationMeta {
        hasNextPage
        hasPrevPage
        page
        perPage
        totalPages
      }
    }
  }
`

const attachShipmentMutation = graphql`
  mutation MessageListAttachShipmentMutation($input: infoAttachMessageShipmentInput!) {
    infoAttachMessageShipment(input: $input) {
      clientMutationId
    }
  }
`

const markReadMutation = graphql`
  mutation MessageListMarkReadMutation($input: infoMarkMessagesReadInput!) {
    infoMarkMessagesRead(input: $input) {
      clientMutationId
    }
  }
`

const sendMessageMutation = graphql`
  mutation MessageListSendMessageMutation($input: infoDirectMessageInput!) {
    infoDirectMessage(input: $input) {
      clientMutationId
      slugs
    }
  }
`
export default memo(MessageListComponent)
